import { HiSolidBolt } from "solid-icons/hi";
import {
  type Component,
  createMemo,
  createSignal,
  For,
  onCleanup,
  onMount,
  Show,
} from "solid-js";
import { useI18n } from "@/api/i18n";
import { useCampaign } from "../api";
import { computeCountdown, parseEndsAt } from "../utils/countdown";

const pad = (n: number) => String(n).padStart(2, "0");

const CountdownCell: Component<{ value: number; label: string }> = (props) => (
  <div class="flex flex-1 flex-col items-center rounded-[9px] bg-white/[0.06] px-1 py-[9px]">
    <span class="font-display text-[22px] font-semibold leading-none tabular-nums text-white">
      {pad(props.value)}
    </span>
    <span class="mt-[5px] text-[9.5px] uppercase tracking-[0.07em] text-white/45">
      {props.label}
    </span>
  </div>
);

/**
 * Early-adopter campaign banner with a countdown to `campaign.ends_at`.
 *
 * Dark promotional surface matching the pricing design: a "−{percent}%" headline
 * on the left and a countdown box on the right. Renders only for an authenticated
 * eligible user while the campaign is active and the end date is in the future —
 * anonymous/non-eligible users (no campaign data) and an expired window show nothing.
 * No "posti rimasti" seats element: the backend campaign payload carries no seat
 * counts, so the banner shows the fixed-end-date countdown only.
 */
export function CampaignBanner() {
  const { t } = useI18n();
  const campaignQuery = useCampaign();
  const campaign = () => campaignQuery.data?.campaign;

  const [now, setNow] = createSignal(Date.now());
  onMount(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    onCleanup(() => clearInterval(id));
  });

  const countdown = createMemo(() => {
    const endsAt = parseEndsAt(campaign()?.ends_at);
    return endsAt === null ? null : computeCountdown(endsAt, now());
  });

  const shouldShow = () => {
    const c = campaign();
    if (!c?.active || !c.eligible) return false;
    const cd = countdown();
    return cd !== null && !cd.expired;
  };

  return (
    <Show when={shouldShow()}>
      <div class="relative overflow-hidden rounded-[18px] bg-[#100a1b] px-8 py-7 shadow-[0_16px_36px_-20px_rgba(16,10,27,0.6)]">
        <div class="pointer-events-none absolute -right-[70px] -top-[90px] h-[280px] w-[280px] rounded-full bg-[radial-gradient(circle,rgba(255,214,44,0.18),transparent_70%)]" />
        <div class="relative grid grid-cols-1 items-center gap-[30px] md:grid-cols-[1.35fr_1fr]">
          {/* left: the offer */}
          <div>
            <div class="mb-3 inline-flex items-center gap-2 text-[11.5px] font-bold uppercase tracking-[0.14em] text-[#ffd62c]">
              <HiSolidBolt class="h-[15px] w-[15px]" />
              {t("billing.campaign.eyebrow")}
            </div>
            <div class="flex flex-wrap items-baseline gap-[14px]">
              <div class="font-display text-[58px] font-semibold leading-[0.9] tracking-[-0.03em] text-[#ffd62c]">
                −{campaign()?.discount_percent ?? 0}%
              </div>
              <div class="whitespace-pre-line font-display text-[19px] font-semibold leading-[1.15] text-white">
                {t("billing.campaign.tagline")}
              </div>
            </div>
            <p class="mt-[14px] max-w-[430px] text-[13.5px] leading-[1.5] text-white/60">
              {t("billing.campaign.note")}
            </p>
          </div>

          {/* right: countdown */}
          <Show when={countdown()}>
            {(cd) => (
              <div class="rounded-[14px] border border-white/10 bg-white/5 px-5 py-[18px]">
                <div class="mb-[9px] text-[11px] uppercase tracking-[0.1em] text-white/45">
                  {t("billing.campaign.ends_in")}
                </div>
                <div class="flex gap-[7px]">
                  <For
                    each={[
                      {
                        value: cd().days,
                        label: t("billing.campaign.unit.days"),
                      },
                      {
                        value: cd().hours,
                        label: t("billing.campaign.unit.hours"),
                      },
                      {
                        value: cd().minutes,
                        label: t("billing.campaign.unit.minutes"),
                      },
                      {
                        value: cd().seconds,
                        label: t("billing.campaign.unit.seconds"),
                      },
                    ]}
                  >
                    {(cell) => (
                      <CountdownCell value={cell.value} label={cell.label} />
                    )}
                  </For>
                </div>
              </div>
            )}
          </Show>
        </div>
      </div>
    </Show>
  );
}
